/**
 * `Swatch` (W1-12 ui-kit-i18n; contract SwatchProps): a colour sample. RGB triples (0..255) or any CSS colour
 * string (e.g. a design token `var(--color-note-tap)`). Labelled swatches are images with an accessible name;
 * unlabelled RGB swatches are named by their hex code so the colour is never conveyed by colour alone.
 */

import { useT } from '../i18n/useT';
import type { RGB } from '../core/util/types';
import { useTr } from './bridge';
import { cx } from './internal/cx';
import { rgbToHex } from './internal/color';
import s from './Swatch.module.css';
import type { SwatchProps } from './types';
import { m } from './ui.messages';

/** CSS colour of a swatch value. */
export function swatchCss(color: RGB | string): string {
  if (typeof color === 'string') return color;
  const [r, g, b] = color;
  return `rgb(${String(r)} ${String(g)} ${String(b)})`;
}

export function Swatch(props: SwatchProps & { readonly className?: string | undefined }) {
  const t = useT();
  const tr = useTr();
  const size = props.size ?? 16;
  const name =
    props.label !== undefined
      ? tr(props.label)
      : typeof props.color === 'string'
        ? undefined
        : t(m['color.value'], { hex: rgbToHex(props.color) });
  return (
    <span
      className={cx(s.swatch, props.className)}
      role={name === undefined ? undefined : 'img'}
      aria-label={name}
      aria-hidden={name === undefined ? true : undefined}
      style={{
        width: `calc(${String(size)}px * var(--ui-scale, 1))`,
        height: `calc(${String(size)}px * var(--ui-scale, 1))`,
        background: swatchCss(props.color),
      }}
    />
  );
}
